import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';

const Mark = styled.div<{
  $type: 'revolut' | 'swedbank' | 'gpm' | '1k' | '5k';
}>`
  width: 25px;
  height: 16px;
  background: #f1eff1;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  ${({ $type }) =>
    $type === 'revolut' &&
    css`
      background: #f1eff1;
    `}
  ${({ $type }) =>
    $type === 'swedbank' &&
    css`
      background: ${({ theme }) => theme.colors.researchLight};
    `}
    ${({ $type }) =>
    $type === 'gpm' &&
    css`
      background: #ecf9f1;
    `}
    ${({ $type }) =>
    $type === '1k' &&
    css`
      background: ${({ theme }) => theme.colors.productionLight};
    `}
    ${({ $type }) =>
    $type === '5k' &&
    css`
      background: ${({ theme }) => theme.colors.errorLight};
    `}
`;

const Dot = styled.div<{ $type: 'revolut' | 'swedbank' | 'gpm' | '1k' | '5k' }>`
  height: 4px;
  width: 4px;
  border-radius: 50%;
  ${({ $type }) =>
    $type === 'revolut' &&
    css`
      background-color: ${({ theme }) => theme.colors.distributionPurple};
    `}
  ${({ $type }) =>
    $type === 'swedbank' &&
    css`
      background-color: ${({ theme }) => theme.colors.research};
    `}
    ${({ $type }) =>
    $type === 'gpm' &&
    css`
      background-color: ${({ theme }) => theme.colors.success};
    `}
    ${({ $type }) =>
    $type === '1k' &&
    css`
      background-color: ${({ theme }) => theme.colors.productionLight};
    `}
    ${({ $type }) =>
    $type === '5k' &&
    css`
      background-color: ${({ theme }) => theme.colors.errorLight};
    `}
`;

const TitleText = styled(Text)`
  margin-bottom: 6px;
  svg {
    margin-right: 8px;
    transform: translateY(1px);
  }
`;

const Content = styled(Text)`
  padding-left: 20px;
`;

interface BoxProps {
  $left: number;
  $top: number;
}

const TooltipBox = styled.div<BoxProps>`
  position: fixed;
  padding: 12px;
  width: 220px;
  z-index: 11;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  white-space: normal;
  text-align: left;
  top: ${({ $top }) => $top - 110}px;
  left: ${({ $left }) => $left + 20}px;
`;

const Triangle = styled.div`
  width: 26px;
  height: 14px;
  position: absolute;
  overflow: hidden;
  bottom: -14px;
  left: 4px;
  margin: auto;
  &::after {
    content: '';
    position: absolute;
    width: 14px;
    height: 14px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: -8px;
    left: 0;
    right: 0;
    margin: auto;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const MarkText = styled(Text)<{ $moneyType: '1k' | '5k' }>`
  ${({ $moneyType }) =>
    $moneyType === '1k' &&
    css`
      color: ${({ theme }) => theme.colors.production};
    `}
  ${({ $moneyType }) =>
    $moneyType === '5k' &&
    css`
      color: ${({ theme }) => theme.colors.error};
    `}
`;

const modalRoot = document.getElementById('modals-root');

interface Props {
  type: 'revolut' | 'swedbank' | 'gpm' | '1k' | '5k';
}

const InvoiceMark: FC<Props> = ({ type }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const ref = useRef(document.createElement('div'));

  const buttonRef = useRef<HTMLDivElement>(null);

  const componentRef = useRef<HTMLDivElement>(null);

  const domRect = buttonRef.current?.getBoundingClientRect();

  const renderText = (type: 'revolut' | 'swedbank' | 'gpm' | '1k' | '5k') => {
    switch (type) {
      case 'swedbank':
        return 'The recipient country uses a different IBAN format, and bulk payments cannot be made.';
      case 'revolut':
        return 'Revolut payments cannot be made to recipients in this country.';
      case 'gpm':
        return 'The invoice has been marked with a 15% exclusion for taxes (GPM) on the total amount.';
    }
  };

  useEffect(() => {
    const callback = () => setShowTooltip(false);
    window.addEventListener('scroll', callback, true);
    return () => {
      window.removeEventListener('scroll', callback, true);
    };
  }, []);

  useEffect(() => {
    const el = ref.current;
    modalRoot!.appendChild(el);
    return () => {
      modalRoot!.removeChild(el);
    };
  }, []);

  return (
    <>
      <Mark
        $type={type}
        onMouseEnter={(e) => {
          e.stopPropagation();
          setShowTooltip(true);
        }}
        onMouseLeave={(e) => {
          e.stopPropagation();
          setShowTooltip(false);
        }}
        ref={buttonRef}
      >
        {type === '1k' || type === '5k' ? (
          <MarkText $moneyType={type} $type="extraSmall">
            {type === '1k' ? '+1k' : '+5k'}
          </MarkText>
        ) : (
          <Dot $type={type} />
        )}
      </Mark>
      {createPortal(
        <>
          {domRect && showTooltip && !!renderText(type) && (
            <TooltipBox
              ref={componentRef}
              $left={domRect.x - domRect.width}
              $top={domRect.y + window.scrollY}
            >
              <Triangle />
              <TitleText $type="smallBold">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="6"
                    cy="5.99988"
                    r="5.5"
                    fill="#4136CF"
                    stroke="#4136CF"
                  />
                  <path
                    d="M5.5 4.30135V3.37488H6.5V4.30135H5.5ZM5.5 8.62488V4.919H6.5V8.62488H5.5Z"
                    fill="white"
                  />
                </svg>
                Note
              </TitleText>
              <Content $type="smallNormal">{renderText(type)}</Content>
            </TooltipBox>
          )}
        </>,
        ref.current,
      )}
    </>
  );
};

export default InvoiceMark;
